import { useState, useEffect } from "react";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
} from "@/components/ui/dialog";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Copy, Share2, X, Link as LinkIcon, ShieldCheck, RefreshCw } from "lucide-react";
import { track } from "@/lib/analytics";

/**
 * v2.2 — Tokenized read-only share. NO email delivery.
 * Surfaces:
 *   • Native OS share sheet on mobile via navigator.share()
 *   • Copy-to-clipboard fallback on desktop
 *   • Existing share links list (with revoke + copy)
 */
export default function ShareReportModal({ open, onClose, reportId, propertyAddress }) {
    const [creating, setCreating] = useState(false);
    const [shares, setShares] = useState([]);
    const [activeShare, setActiveShare] = useState(null);  // most recent created
    const [days, setDays] = useState(30);

    const canNativeShare = typeof navigator !== "undefined" && typeof navigator.share === "function";

    const loadShares = async () => {
        try {
            const r = await api.get(`/reports/${reportId}/shares`);
            setShares(r.data.shares || []);
        } catch { /* non-critical */ }
    };

    useEffect(() => {
        if (open) {
            loadShares();
            setActiveShare(null);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, reportId]);

    const createShare = async () => {
        try {
            setCreating(true);
            const r = await api.post(`/reports/${reportId}/share`, {
                expires_in_days: Number(days) || 30,
            });
            track("share_link_created", { report_id: reportId, expires_in_days: days });
            setActiveShare({
                url: r.data.share_url,
                token: r.data.token,
                expires_at: r.data.expires_at,
            });
            await loadShares();
            // Auto-copy for desktop convenience
            try {
                await navigator.clipboard.writeText(r.data.share_url);
                toast.success("Share link created and copied.");
            } catch {
                toast.success("Share link created.");
            }
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Could not create share link.");
        } finally {
            setCreating(false);
        }
    };

    const handleNativeShare = async (url) => {
        try {
            await navigator.share({
                title: `Inspection report${propertyAddress ? ` — ${propertyAddress}` : ""}`,
                text: "Buyer's Leverage inspection report (read-only)",
                url,
            });
            track("share_link_native_shared", { report_id: reportId });
        } catch (e) {
            // user cancelled — silent
        }
    };

    const copyLink = async (url) => {
        try {
            await navigator.clipboard.writeText(url);
            toast.success("Link copied");
            track("share_link_copied", { report_id: reportId });
        } catch {
            toast.error("Could not copy link.");
        }
    };

    const revoke = async (token) => {
        try {
            await api.delete(`/reports/${reportId}/share/${token}`);
            toast.success("Share revoked.");
            if (activeShare?.token === token) setActiveShare(null);
            loadShares();
        } catch {
            toast.error("Could not revoke share.");
        }
    };

    return (
        <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
            <DialogContent
                className="max-w-lg bg-bl-surface border-bl rounded-2xl"
                data-testid="share-modal"
            >
                <DialogHeader>
                    <div className="bl-eyebrow w-fit flex items-center gap-1.5">
                        <Share2 className="w-3.5 h-3.5" /> Share read-only
                    </div>
                    <DialogTitle className="font-display text-2xl text-bl mt-2">
                        Share this report
                    </DialogTitle>
                    <DialogDescription className="text-bl-muted">
                        Generates a secure, read-only link. You decide who to send it to —
                        we don't email anyone on your behalf.
                    </DialogDescription>
                </DialogHeader>

                {!activeShare && (
                    <div className="space-y-3 mt-2" data-testid="share-create-form">
                        <div className="bl-card p-4 bg-bl-bg/40 flex items-start gap-2.5">
                            <ShieldCheck className="w-4 h-4 text-bl-primary mt-0.5 flex-shrink-0" />
                            <p className="text-bl text-sm leading-relaxed">
                                The link is unguessable and view-only. Anyone with it can read
                                the report but can't access your dashboard, payment info, or
                                account data.
                            </p>
                        </div>
                        <div className="flex items-center gap-3">
                            <label className="text-[11px] tracking-[0.18em] uppercase text-bl-muted">
                                Expires in
                            </label>
                            <select
                                value={days}
                                onChange={(e) => setDays(e.target.value)}
                                className="px-3 py-2 bg-bl-bg rounded-lg border border-bl text-bl text-sm"
                                data-testid="share-expiry-select"
                            >
                                <option value={1}>1 day</option>
                                <option value={7}>7 days</option>
                                <option value={30}>30 days</option>
                                <option value={60}>60 days</option>
                                <option value={90}>90 days</option>
                            </select>
                        </div>
                        <button
                            onClick={createShare}
                            disabled={creating}
                            className="bl-btn-primary w-full"
                            data-testid="share-create-btn"
                        >
                            <LinkIcon className="w-4 h-4" />
                            {creating ? "Creating link…" : "Create share link"}
                        </button>
                    </div>
                )}

                {activeShare && (
                    <div className="space-y-3 mt-2" data-testid="share-success">
                        <div className="bl-card p-4 bg-bl-bg/40">
                            <div className="text-[10px] tracking-[0.2em] uppercase text-bl-muted mb-2">
                                Your share link
                            </div>
                            <div
                                className="font-mono-bl text-xs text-bl break-all"
                                data-testid="share-url"
                            >
                                {activeShare.url}
                            </div>
                            <div className="text-bl-muted text-[11px] mt-2">
                                Expires {new Date(activeShare.expires_at).toLocaleDateString()}
                            </div>
                        </div>
                        <div className="flex gap-2 flex-wrap">
                            {canNativeShare && (
                                <button
                                    onClick={() => handleNativeShare(activeShare.url)}
                                    className="bl-btn-primary flex-1"
                                    data-testid="share-native-btn"
                                >
                                    <Share2 className="w-4 h-4" /> Share
                                </button>
                            )}
                            <button
                                onClick={() => copyLink(activeShare.url)}
                                className={canNativeShare ? "bl-btn-secondary flex-1" : "bl-btn-primary flex-1"}
                                data-testid="share-copy-btn"
                            >
                                <Copy className="w-4 h-4" /> Copy link
                            </button>
                        </div>
                        <button
                            onClick={() => setActiveShare(null)}
                            className="text-bl-muted hover:text-bl text-xs flex items-center gap-1"
                            data-testid="share-new-btn"
                        >
                            <RefreshCw className="w-3 h-3" /> Create another
                        </button>
                    </div>
                )}

                {shares.length > 0 && (
                    <div className="mt-6 border-t border-bl pt-5">
                        <div className="bl-eyebrow mb-3">Existing links</div>
                        <div className="space-y-2 max-h-56 overflow-y-auto">
                            {shares.map((s) => {
                                const url = `${window.location.origin}/shared/${s.token}`;
                                const expired = new Date(s.expires_at) < new Date();
                                return (
                                    <div
                                        key={s.share_id}
                                        className={
                                            "border border-bl rounded-lg p-3 text-sm " +
                                            (s.revoked || expired ? "opacity-60" : "")
                                        }
                                        data-testid={`share-row-${s.share_id}`}
                                    >
                                        <div className="flex items-start justify-between gap-3">
                                            <div className="min-w-0 flex-1">
                                                <div className="text-bl truncate font-mono-bl text-xs">
                                                    {url}
                                                </div>
                                                <div className="text-bl-muted text-xs mt-1">
                                                    Expires{" "}
                                                    {new Date(s.expires_at).toLocaleDateString()}{" "}
                                                    · {s.view_count} views
                                                    {s.revoked && " · revoked"}
                                                </div>
                                            </div>
                                            <div className="flex gap-1">
                                                {!s.revoked && !expired && canNativeShare && (
                                                    <button
                                                        onClick={() => handleNativeShare(url)}
                                                        className="p-1.5 text-bl-muted hover:text-bl-primary"
                                                        title="Share"
                                                        data-testid={`share-native-${s.share_id}`}
                                                    >
                                                        <Share2 className="w-4 h-4" />
                                                    </button>
                                                )}
                                                <button
                                                    onClick={() => copyLink(url)}
                                                    className="p-1.5 text-bl-muted hover:text-bl-primary"
                                                    title="Copy link"
                                                    data-testid={`share-copy-${s.share_id}`}
                                                >
                                                    <Copy className="w-4 h-4" />
                                                </button>
                                                {!s.revoked && !expired && (
                                                    <button
                                                        onClick={() => revoke(s.token)}
                                                        className="p-1.5 text-bl-muted hover:text-[var(--bl-rust)]"
                                                        title="Revoke"
                                                        data-testid={`share-revoke-${s.share_id}`}
                                                    >
                                                        <X className="w-4 h-4" />
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
}
